import React, { useContext, useState, useEffect } from "react";
import PaginationButton from "../../commanComponents/buttons/PaginationButton";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import { communication } from "../../constants/apis/communication";
import useDebounce from "../../constants/helpers/useDebounce";
import ColorBtn from "../../commanComponents/buttons/ColorBtn";
import Loader from "../../commanComponents/Loader";
import { PaginationContext } from "../../constants/context/PaginationProvider";
import CustomModal from "../../commanComponents/CustomModal";
import SelectMine from "../../commanComponents/SelectMine";
import { InputBox } from "../../commanComponents/inputs/InputBox";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { getUserDetails } from "../../constants/helpers/getUserDetails";

const qrIcon = (
  <svg
    width="36"
    height="36"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ffffff"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ backgroundColor: "#2f3299", padding: "6px", borderRadius: "8px" }}
  >
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M3 17v2a2 2 0 0 1 2 2h2" />
    <rect x="7" y="7" width="10" height="10" rx="1" />
  </svg>
);

export default function EquipmentQrCodeComponent() {
  const { equipmentPage, setEquipmentPage, globalSearchString } = useContext(PaginationContext);
  const debouncedSearchTerm = useDebounce(globalSearchString, 1000, setEquipmentPage);

  const pageLimit = process.env.REACT_APP_PUBLIC_LIMIT;
  const [open, setOpen] = useState({ isOpen: false });
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeError, setBarcodeError] = useState("");
  const [isCheckingBarcode, setIsCheckingBarcode] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [selectedMine, setSelectedMine] = useState(getUserDetails()?.mineId || "");

  useEffect(() => {
    const checkBarcodeUniqueness = async () => {
      if (!barcodeInput.trim()) {
        setBarcodeError("");
        return;
      }

      const barcodeRegex = /^[A-Za-z]{2}\d{4}[A-Za-z]{4}$/;
      if (!barcodeRegex.test(barcodeInput.trim())) {
        setBarcodeError("Barcode must start with 2 letters, followed by 4 numbers, and end with 4 letters (e.g., AB1234CDEF)");
        return;
      }

      setIsCheckingBarcode(true);
      try {
        const response = await communication.checkBarcode({ barcode: barcodeInput.trim(), mineId: selectedMine });
        if (response?.data?.status === "SUCCESS" && response?.data?.exists) {
          setBarcodeError("Barcode already exists");
        } else if (response?.data?.status === "FAILED") {
          setBarcodeError(response?.data?.message || "Error checking barcode");
        } else {
          setBarcodeError("");
        }
      } catch (error) {
        setBarcodeError("Failed to verify barcode uniqueness");
      } finally {
        setIsCheckingBarcode(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      if (open.isOpen && !selectedEquipment?.equipmentBarcode) {
        checkBarcodeUniqueness();
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [barcodeInput, selectedEquipment, open.isOpen]);

  const generateBarcode = async (id) => {
    try {
      const response = await communication.getEquipmentById({ id, mineId: selectedMine });
      if (response?.data?.status === "SUCCESS") {
        const eq = response.data.result;
        setSelectedEquipment(eq);

        let initialBarcode = eq.equipmentBarcode;
        if (!initialBarcode) {
          const mineIdLetters = (eq.planningPlant || "")
            .replace(/[^A-Za-z]/g, "")
            .substring(0, 2)
            .toUpperCase()
            .padEnd(2, "X");
          const eqIdDigits = (eq.equipmentId || "")
            .replace(/[^0-9]/g, "")
            .slice(-4)
            .padStart(4, "0");
          const catDescLetters = (eq.equipmentCategoryDescription || "")
            .replace(/[^A-Za-z]/g, "")
            .substring(0, 4)
            .toUpperCase()
            .padEnd(4, "X");
          initialBarcode = `${mineIdLetters}${eqIdDigits}${catDescLetters}`;
        }

        setBarcodeInput(initialBarcode);
        setBarcodeError("");
        setOpen({ isOpen: true });
      } else {
        toast.error(response?.data?.message || "Failed to fetch equipment details");
      }
    } catch (error) {
      toast.error("An error occurred while fetching equipment details");
    }
  };

  const handleGenerateBarcode = async () => {
    if (!selectedEquipment?._id) return;
    const trimmedBarcode = barcodeInput.trim();
    if (!trimmedBarcode) {
      setBarcodeError("Please enter a Barcode ID");
      return;
    }
    const formattedBarcode = trimmedBarcode.toUpperCase();
    const barcodeRegex = /^[A-Z]{2}\d{4}[A-Z]{4}$/;
    if (!barcodeRegex.test(formattedBarcode)) {
      setBarcodeError("Barcode must start with 2 letters, followed by 4 numbers, and end with 4 letters (e.g., AB1234CDEF)");
      return;
    }
    if (barcodeError || isCheckingBarcode) {
      return;
    }

    setIsCheckingBarcode(true);
    try {
      const checkResponse = await communication.checkBarcode({ barcode: formattedBarcode, mineId: selectedMine });
      if (checkResponse?.data?.status === "SUCCESS" && checkResponse?.data?.exists) {
        setBarcodeError("Barcode already exists");
        setIsCheckingBarcode(false);
        return;
      }

      const response = await communication.updateBarcode({
        id: selectedEquipment._id,
        equipmentBarcode: formattedBarcode,
        mineId: selectedMine,
      });

      if (response?.data?.status === "SUCCESS") {
        toast.success("Barcode updated successfully");
        setOpen({ isOpen: false });
        refetch();
      } else {
        toast.error(response?.data?.message || "Failed to update barcode");
      }
    } catch (error) {
      console.log("Error updating barcode:", error);
      toast.error("An error occurred during barcode update");
    } finally {
      setIsCheckingBarcode(false);
    }
  };

  const downloadBarcodeImage = async (format) => {
    setShowDownloadMenu(false);
    const element = document.getElementById("qr-code-area");
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 4, // Multiplies the resolution for standard high-quality printing
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff", // solid white background for the PNG
      });

      if (format === "pdf") {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`Barcode_${selectedEquipment?.equipmentId || "Equipment"}.pdf`);
        toast.success("Barcode downloaded successfully as PDF");
      } else {
        const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
        const imgData = canvas.toDataURL(mimeType);
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `Barcode_${selectedEquipment?.equipmentId || "Equipment"}.${format === "jpeg" ? "jpg" : "png"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Barcode downloaded successfully as ${format.toUpperCase()}`);
      }
    } catch (error) {
      toast.error("Failed to download Barcode image");
    }
  };

  const {
    data: equipmentList,
    isFetching,
    refetch,
  } = useQuery(
    ["getEquipmentList", equipmentPage, debouncedSearchTerm, selectedMine],
    () => communication?.getEquipmentList({ searchString: debouncedSearchTerm, page: equipmentPage, mineId: selectedMine }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data?.data?.status === "JWT_INVALID") {
          toast.error(`Failed: ${data?.data?.message}`);
        }
      },
      onError: (error) => {
        toast.error(`Failed: ${error?.message}`);
      },
    }
  );
  const userDetails = JSON.parse(sessionStorage.getItem(process.env.REACT_APP_PUBLIC_USERDETAILS));
  console.log(userDetails);

  return (
    <>
      {isFetching && <Loader />}
      <div className="pageWrapper">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3 flex-wrap"></div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <SelectMine selectedMine={selectedMine} onChange={(e) => setSelectedMine(e.target.value)} />
            <PaginationButton setPage={setEquipmentPage} page={equipmentPage} totalPages={equipmentList?.data?.totalPages} />
          </div>
        </div>

        <div className="mt-2">
          <div className="tableWrapper comman-box-shadow" style={{ position: "relative", height: "74vh" }}>
            <table>
              <thead>
                <tr>
                  <th className="text-center col_80px">Sr. No.</th>

                  <th className="col_190px">Equipment Barcode Id</th>
                  <th className="col_120px">Equipment Id</th>
                  {userDetails?.designation === "superadmin" && <th className="col_150px">Mine Code</th>}
                  {/* <th className="col_150px">Mine Code</th> */}
                  <th className="col_200px">Equipment Description</th>

                  <th className="col_300px">Equipment Master Description 2</th>
                  <th className="col_150px">Equipment Category</th>
                  <th className="col_250px">Equipment Category Description</th>
                  <th className="text-center row-fix-to-last col_200px">Action</th>
                </tr>
              </thead>
              <tbody>
                {equipmentList?.data?.equipment?.map((ele, index) => {
                  return (
                    <tr className={index % 2 === 0 && "skyBlueTrBg"}>
                      <td className="text-center">{(equipmentPage - 1) * pageLimit + (index + 1)}</td>

                      <td>{ele?.equipmentBarcode ?? "N/A"}</td>
                      <td>{ele?.equipmentId}</td>
                      {userDetails?.designation === "superadmin" && <td>{ele?.planningPlant}</td>}
                      {/* <td>{ele?.planningPlant}</td> */}
                      <td>{ele?.equipmentDescription}</td>

                      <td>{ele?.equipmentMasterDescription2}</td>
                      <td>{ele?.equipmentCategory}</td>
                      <td>{ele?.equipmentCategoryDescription}</td>

                      <td className="text-center row-fix-to-last col_150px">
                        <ColorBtn
                          text={ele?.equipmentBarcode ? "View Barcode" : "Generate Barcode"}
                          bgColor={ele?.equipmentBarcode ? "#34c759" : "#3a57e8"}
                          color={"#fff"}
                          onClick={() => generateBarcode(ele?._id)}
                          padding="4px 12px"
                          fontSize="12px"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(!equipmentList?.data?.equipment || equipmentList?.data?.equipment?.length === 0) && <h6 className="noDataAvailableWrapper">No Data Available</h6>}
          </div>
        </div>
      </div>

      {open.isOpen && (
        <div className="premiumModalOverlay" onClick={() => setOpen({ isOpen: false })}>
          <div className="premiumModalContent" style={{ maxWidth: selectedEquipment?.equipmentBarcode ? "380px" : "800px", width: "95%" }} onClick={(e) => e.stopPropagation()}>
            <button className="premiumModalCloseBtn" onClick={() => setOpen({ isOpen: false })}>
              &times;
            </button>

            {!selectedEquipment?.equipmentBarcode ? (
              <div className="premiumFormContainer">
                {/* Header */}
                <div className="premiumFormHeader">
                  <h2>{qrIcon} Equipment Details</h2>
                  <p>Check the details and generate a barcode for this equipment</p>
                </div>

                {/* Form Card */}
                <div className="premiumFormCard">
                  <div className="custom-grid-2">
                    <InputBox label="Equipment ID" value={selectedEquipment?.equipmentId || ""} disabled={true} />
                    <InputBox label="Mine ID" value={selectedEquipment?.planningPlant || ""} disabled={true} />
                    <InputBox label="Equipment Description" value={selectedEquipment?.equipmentDescription || ""} disabled={true} />
                    <InputBox label="Equipment Category Description" value={selectedEquipment?.equipmentCategoryDescription || ""} disabled={true} />
                    <InputBox label="Equipment Category" value={selectedEquipment?.equipmentCategory || ""} disabled={true} />
                    <div className="d-flex flex-column">
                      <InputBox
                        label="Barcode ID *"
                        value={barcodeInput}
                        onChange={(e) => {
                          setBarcodeInput(e.target.value);
                          setBarcodeError("");
                        }}
                        placeholder="Enter Barcode ID"
                        errorMessage={barcodeError}
                      />
                      <small className="text-muted mt-1" style={{ fontSize: "11.5px", lineHeight: "1.4" }}>
                        <span className="fw-semibold" style={{ color: "#2f3293" }}>
                          Note:
                        </span>{" "}
                        Bar code generated by 2 digit Mine Id, last 4 digit of Equipment Id and 4 letter of Equipment Category Description.
                      </small>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="premiumButtonsWrapper">
                  <button className="premiumBtnCancel" onClick={() => setOpen({ isOpen: false })}>
                    Cancel
                  </button>
                  <button
                    className="premiumBtnSubmit"
                    onClick={handleGenerateBarcode}
                    disabled={!!barcodeError || isCheckingBarcode}
                    style={{
                      backgroundColor: !!barcodeError || isCheckingBarcode ? "#a8a8a8" : "#2f3299",
                    }}
                  >
                    {isCheckingBarcode ? "Checking..." : "Generate"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "8px 16px 16px" }}>
                {/* Title with divider */}
                <h5 style={{ fontWeight: 700, color: "#2f3299", marginBottom: "12px", fontSize: "18px" }}>View Barcode</h5>
                <hr style={{ borderColor: "#e2e8f0", marginBottom: "24px" }} />

                {/* QR Code and text wrapped inside the printable/downloadable area */}
                <div
                  id="qr-code-area"
                  style={{
                    display: "inline-block",
                    padding: "24px",
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {/* QR Code with corner brackets */}
                  <div style={{ display: "inline-block", position: "relative", padding: "16px" }}>
                    {/* Corner brackets */}
                    {[
                      { top: 0, left: 0, borderTop: "3px solid #2f3299", borderLeft: "3px solid #2f3299" },
                      { top: 0, right: 0, borderTop: "3px solid #2f3299", borderRight: "3px solid #2f3299" },
                      { bottom: 0, left: 0, borderBottom: "3px solid #2f3299", borderLeft: "3px solid #2f3299" },
                      { bottom: 0, right: 0, borderBottom: "3px solid #2f3299", borderRight: "3px solid #2f3299" },
                    ].map((pos, i) => (
                      <div
                        key={i}
                        style={{
                          position: "absolute",
                          width: "20px",
                          height: "20px",
                          ...pos,
                        }}
                      />
                    ))}
                    <QRCode value={selectedEquipment.equipmentBarcode} size={200} fgColor="#2f3299" bgColor="#ffffff" />
                  </div>

                  {/* Barcode ID included inside the downloadable container */}
                  <p style={{ marginTop: "18px", fontSize: "15px", color: "#1a202c", fontWeight: 600 }}>ID : {selectedEquipment.equipmentBarcode}</p>
                </div>

                {/* Action Buttons */}
                <div className="premiumButtonsWrapper" style={{ marginTop: "24px" }}>
                  <button className="premiumBtnCancel" onClick={() => setOpen({ isOpen: false })}>
                    Close
                  </button>
                  <div style={{ position: "relative" }}>
                    <button className="premiumBtnSubmit" onClick={() => setShowDownloadMenu(!showDownloadMenu)}>
                      Download
                    </button>
                    {showDownloadMenu && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "100%",
                          right: 0,
                          backgroundColor: "#fff",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          borderRadius: "8px",
                          padding: "8px 0",
                          zIndex: 100,
                          marginBottom: "8px",
                          minWidth: "140px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <button
                          onClick={() => downloadBarcodeImage("pdf")}
                          style={{ padding: "8px 16px", border: "none", background: "none", textAlign: "left", cursor: "pointer", fontSize: "14px", width: "100%", color: "#333" }}
                          onMouseOver={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
                        >
                          Download as PDF
                        </button>
                        <button
                          onClick={() => downloadBarcodeImage("png")}
                          style={{ padding: "8px 16px", border: "none", background: "none", textAlign: "left", cursor: "pointer", fontSize: "14px", width: "100%", color: "#333" }}
                          onMouseOver={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
                        >
                          Download as PNG
                        </button>
                        <button
                          onClick={() => downloadBarcodeImage("jpeg")}
                          style={{ padding: "8px 16px", border: "none", background: "none", textAlign: "left", cursor: "pointer", fontSize: "14px", width: "100%", color: "#333" }}
                          onMouseOver={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
                        >
                          Download as JPG
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
